import React, { useEffect, useState, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { 
  FileText, Plus, Edit, Trash2, Save, Printer, Download, 
  Send, MessageCircle, Copy, Eye, FolderOpen, Search,
  FileEdit, Tag, Users, GraduationCap, Building, Calendar,
  CreditCard, FileSignature, X, Check, Loader2, Upload
} from 'lucide-react';

export default function DocumentEditor() {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState({});
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Editor states
  const [editorDialogOpen, setEditorDialogOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [editorContent, setEditorContent] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateGroup, setTemplateGroup] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null); // V6.9.1
  const [importDialogOpen, setImportDialogOpen] = useState(false); // V6.10
  // V6.11 · UX premium do editor
  const [zoom, setZoom] = useState(100);
  const [leftOpen, setLeftOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [fieldSearch, setFieldSearch] = useState('');
  
  // Preview/Generate states
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [generating, setGenerating] = useState(false);
  
  // WhatsApp states
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState('');
  
  const quillRef = useRef(null);
  const printRef = useRef(null);

  // Quill modules configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['blockquote', 'code-block'],
      ['clean']
    ]
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const [templatesRes, groupsRes, tagsRes] = await Promise.all([
        api.get('/document-templates?active_only=false'),
        api.get('/document-templates/groups'),
        api.get('/document-templates/tags')
      ]);
      // V6.9.1 · Guardas defensivas contra shapes inesperados (produção sem seed).
      setTemplates(Array.isArray(templatesRes?.data) ? templatesRes.data : []);
      setGroups(Array.isArray(groupsRes?.data) ? groupsRes.data : []);
      setTags(tagsRes?.data && typeof tagsRes.data === 'object' ? tagsRes.data : {});
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setLoadError(error?.response?.data?.detail || error?.message || 'Falha ao carregar');
      setTemplates([]); setGroups([]); setTags({});
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async (search = '') => {
    try {
      setLoadingStudents(true);
      const res = await api.get(`/students?search=${search}&limit=50`);
      setStudents(res.data);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleNewTemplate = () => {
    setCurrentTemplate(null);
    setTemplateName('');
    setTemplateGroup('Matrícula');
    setTemplateDescription('');
    setEditorContent('<p><br></p>');
    setEditorDialogOpen(true);
  };

  const handleEditTemplate = (template) => {
    setCurrentTemplate(template);
    setTemplateName(template.name);
    setTemplateGroup(template.group);
    setTemplateDescription(template.description || '');
    setEditorContent(template.content);
    setEditorDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Informe o nome do template');
      return;
    }
    if (!templateGroup) {
      toast.error('Selecione um grupo');
      return;
    }

    try {
      setSaving(true);
      const data = {
        name: templateName,
        group: templateGroup,
        content: editorContent,
        description: templateDescription
      };

      if (currentTemplate) {
        await api.put(`/document-templates/${currentTemplate.id}`, data);
        toast.success('Template atualizado com sucesso');
      } else {
        await api.post('/document-templates', data);
        toast.success('Template criado com sucesso');
      }

      setEditorDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      toast.error('Erro ao salvar template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Deseja excluir o template "${template.name}"?`)) return;

    try {
      await api.delete(`/document-templates/${template.id}`);
      toast.success('Template excluído');
      loadData();
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      toast.error('Erro ao excluir template');
    }
  };

  const handleInsertTag = (tag) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      quill.insertText(range.index, tag);
      quill.setSelection(range.index + tag.length);
    }
  };

  const handlePreviewTemplate = async (template) => {
    setCurrentTemplate(template);
    setSelectedStudent(null);
    setPreviewContent('');
    setStudentSearch('');
    await loadStudents();
    setPreviewDialogOpen(true);
  };

  const handleGenerateDocument = async () => {
    if (!selectedStudent) {
      toast.error('Selecione um aluno');
      return;
    }

    try {
      setGenerating(true);
      const res = await api.post(`/document-templates/${currentTemplate.id}/generate?student_id=${selectedStudent.id}`);
      setPreviewContent(res.data.content);
    } catch (error) {
      console.error('Erro ao gerar documento:', error);
      toast.error('Erro ao gerar documento');
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${currentTemplate?.name || 'Documento'}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            padding: 40px; 
            max-width: 800px; 
            margin: 0 auto;
            line-height: 1.6;
          }
          @media print {
            body { padding: 20px; }
          }
        </style>
      </head>
      <body>
        ${previewContent}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPDF = async () => {
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const element = document.createElement('div');
      element.innerHTML = previewContent;
      element.style.padding = '40px';
      element.style.fontFamily = 'Arial, sans-serif';
      element.style.lineHeight = '1.6';
      
      const opt = {
        margin: 10,
        filename: `${currentTemplate?.name || 'documento'}_${selectedStudent?.name || ''}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      
      await html2pdf().set(opt).from(element).save();
      toast.success('PDF baixado com sucesso');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  const handleSendWhatsApp = async () => {
    if (!selectedStudent) {
      toast.error('Selecione um aluno');
      return;
    }

    try {
      const res = await api.post(`/document-templates/${currentTemplate.id}/whatsapp?student_id=${selectedStudent.id}`);
      setWhatsappLink(res.data.whatsapp_link);
      setWhatsappDialogOpen(true);
    } catch (error) {
      console.error('Erro ao gerar link do WhatsApp:', error);
      toast.error(error.response?.data?.detail || 'Erro ao gerar link do WhatsApp');
    }
  };

  const filteredTemplates = (Array.isArray(templates) ? templates : []).filter(t => {
    if (!t) return false;
    const matchGroup = selectedGroup === 'all' || t.group === selectedGroup;
    const matchSearch = !searchTerm ||
      (t.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchGroup && matchSearch;
  });

  const getGroupIcon = (group) => {
    switch (group) {
      case 'Matrícula': return <GraduationCap className="w-4 h-4" />;
      case 'Contratos': return <FileSignature className="w-4 h-4" />;
      case 'Financeiro': return <CreditCard className="w-4 h-4" />;
      case 'Declarações': return <FileText className="w-4 h-4" />;
      case 'Autorizações': return <Check className="w-4 h-4" />;
      default: return <FolderOpen className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="document-editor-title">
            <FileEdit className="w-7 h-7 text-purple-600" />
            Editor de Documentos
          </h1>
          <p className="text-muted-foreground mt-1">
            Crie e gerencie templates de documentos com campos dinâmicos
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="import-template-btn">
            <Upload className="w-4 h-4 mr-2" />
            Importar Template
          </Button>
          <Button onClick={handleNewTemplate} data-testid="new-template-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Template
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={selectedGroup} onValueChange={setSelectedGroup}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Filtrar por grupo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Grupos</SelectItem>
            {(groups || []).map(group => (
              <SelectItem key={group} value={group}>
                <div className="flex items-center gap-2">
                  {getGroupIcon(group)}
                  {group}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <Card className="p-12 text-center" data-testid="templates-empty">
          <FileText className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">
            {loadError ? 'Não foi possível carregar os documentos' : 'Nenhum template encontrado'}
          </h3>
          <p className="text-muted-foreground mt-1">
            {loadError ? loadError : 'Crie seu primeiro template clicando no botão acima'}
          </p>
          {loadError && (
            <Button variant="outline" className="mt-4" onClick={loadData} data-testid="btn-retry-templates">
              Tentar novamente
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => (
            <Card key={template.id} className="hover:border-primary transition-colors">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {getGroupIcon(template.group)}
                    <Badge variant="outline">{template.group}</Badge>
                  </div>
                  {!template.active && (
                    <Badge variant="secondary">Inativo</Badge>
                  )}
                </div>
                <CardTitle className="text-lg mt-2">{template.name}</CardTitle>
                {template.description && (
                  <CardDescription className="line-clamp-2">
                    {template.description}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                  <Tag className="w-3 h-3" />
                  <span>{template.tags_used?.length || 0} campos dinâmicos</span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handlePreviewTemplate(template)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Usar
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEditTemplate(template)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDeleteTemplate(template)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Editor Dialog · V6.11 · redesign premium (só UX) */}
      <Dialog open={editorDialogOpen} onOpenChange={setEditorDialogOpen}>
        <DialogContent className="max-w-[98vw] w-full max-h-[96vh] p-0 gap-0 overflow-hidden bg-[#F7F8FC]" data-testid="editor-dialog">
          {/* Header sticky com título + salvar */}
          <div className="flex items-center justify-between px-5 py-3 bg-white border-b shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <FileEdit className="w-5 h-5 text-purple-600 shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground">Editor de Documentos</div>
                <div className="font-semibold truncate">{templateName || (currentTemplate ? 'Editar Template' : 'Novo Template')}</div>
                <div className="text-[11px] text-muted-foreground">{templateGroup || '—'}</div>
              </div>
              <button onClick={() => setSettingsOpen((v) => !v)} className="ml-2 text-xs text-purple-600 hover:underline" data-testid="btn-tpl-settings">⚙ Configurações</button>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center border rounded px-1 h-8 bg-white">
                <button onClick={() => setZoom((z) => Math.max(60, z - 10))} className="px-2 text-sm">−</button>
                <span className="text-xs w-10 text-center">{zoom}%</span>
                <button onClick={() => setZoom((z) => Math.min(150, z + 10))} className="px-2 text-sm">+</button>
              </div>
              <Button variant="outline" size="sm" onClick={() => setLeftOpen((v) => !v)} data-testid="btn-toggle-left">
                {leftOpen ? '← Recolher' : '→ Campos'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditorDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSaveTemplate} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-save-header">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando...</> : <><Save className="w-4 h-4 mr-2" />Salvar Template</>}
              </Button>
            </div>
          </div>

          {/* Painel de configurações inline */}
          {settingsOpen && (
            <div className="bg-white border-b px-5 py-3 grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Nome</Label><Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} /></div>
              <div><Label className="text-xs">Grupo</Label>
                <Select value={templateGroup} onValueChange={setTemplateGroup}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(groups || []).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Descrição</Label><Input value={templateDescription} onChange={(e) => setTemplateDescription(e.target.value)} /></div>
            </div>
          )}

          {/* Corpo — 2 colunas */}
          <div className="flex flex-1 min-h-0" style={{ height: 'calc(96vh - 68px)' }}>
            {/* Painel Campos */}
            {leftOpen && (
              <aside className="w-[300px] shrink-0 bg-white border-r flex flex-col">
                <div className="p-3 border-b">
                  <div className="text-sm font-medium flex items-center gap-2 mb-2"><Tag className="w-4 h-4" /> Campos Disponíveis</div>
                  <Input placeholder="Buscar campo..." value={fieldSearch} onChange={(e) => setFieldSearch(e.target.value)} className="h-8" data-testid="field-search" />
                </div>
                <ScrollArea className="flex-1">
                  <Accordion type="multiple" className="w-full">
                    {Object.entries(tags || {}).map(([key, category]) => {
                      const filtered = (category?.tags || []).filter((t) => {
                        if (!fieldSearch) return true;
                        const q = fieldSearch.toLowerCase();
                        return (t.label || '').toLowerCase().includes(q) || (t.tag || '').toLowerCase().includes(q);
                      });
                      if (fieldSearch && filtered.length === 0) return null;
                      return (
                        <AccordionItem key={key} value={key}>
                          <AccordionTrigger className="px-3 py-2 text-sm hover:no-underline">{category?.label || key}</AccordionTrigger>
                          <AccordionContent>
                            <div className="flex flex-wrap gap-1 px-2 pb-2">
                              {filtered.map((tag, idx) => (
                                <button key={idx} onClick={() => handleInsertTag(tag.tag)}
                                  className="text-xs px-2 py-1 rounded-full border bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200 transition-colors" title={tag.tag}>
                                  {tag.label}
                                </button>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </ScrollArea>
              </aside>
            )}

            {/* Área do documento — fundo cinza + página A4 branca centralizada */}
            <main className="flex-1 min-w-0 overflow-auto" style={{ background: '#F7F8FC' }}>
              <div className="mx-auto py-6" style={{ width: `${(210 * zoom) / 100 + 40}mm`, maxWidth: '100%' }}>
                <div className="bg-white shadow-lg rounded-md" style={{
                  width: `${(210 * zoom) / 100}mm`,
                  minHeight: `${(297 * zoom) / 100}mm`,
                  padding: '20mm',
                  margin: '0 auto',
                  transformOrigin: 'top center',
                  fontSize: `${(11 * zoom) / 100}pt`,
                }}>
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={editorContent}
                    onChange={setEditorContent}
                    modules={quillModules}
                    className="dna-tpl-quill"
                  />
                </div>
              </div>
            </main>
          </div>

        </DialogContent>
      </Dialog>

      {/* Preview/Generate Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-[95vw] w-full max-h-[95vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {currentTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              Selecione um aluno para gerar o documento preenchido
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(95vh-200px)]">
            {/* Seleção de Aluno */}
            <div className="border rounded-lg overflow-hidden">
              <div className="p-3 bg-muted border-b">
                <h3 className="font-medium flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Selecionar Aluno
                </h3>
              </div>
              <div className="p-3 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar aluno..."
                    value={studentSearch}
                    onChange={(e) => {
                      setStudentSearch(e.target.value);
                      loadStudents(e.target.value);
                    }}
                    className="pl-10"
                  />
                </div>
              </div>
              <ScrollArea className="h-[calc(100%-120px)]">
                <div className="p-2 space-y-1">
                  {loadingStudents ? (
                    <div className="p-4 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </div>
                  ) : students.length === 0 ? (
                    <p className="p-4 text-center text-muted-foreground text-sm">
                      Nenhum aluno encontrado
                    </p>
                  ) : (
                    students.map(student => (
                      <button
                        key={student.id}
                        onClick={() => setSelectedStudent(student)}
                        className={`w-full text-left p-2 rounded-lg transition-colors ${
                          selectedStudent?.id === student.id 
                            ? 'bg-primary text-primary-foreground' 
                            : 'hover:bg-muted'
                        }`}
                      >
                        <p className="font-medium text-sm">{student.name}</p>
                        <p className={`text-xs ${selectedStudent?.id === student.id ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                          {student.class_name || 'Sem turma'} • RM: {student.registration_number}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Preview do Documento */}
            <div className="lg:col-span-2 border rounded-lg overflow-hidden flex flex-col">
              <div className="p-3 bg-muted border-b flex items-center justify-between">
                <h3 className="font-medium">Preview do Documento</h3>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={handleGenerateDocument}
                    disabled={!selectedStudent || generating}
                  >
                    {generating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-1" />
                        Gerar
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <ScrollArea className="flex-1 p-6 bg-white">
                {previewContent ? (
                  <div 
                    ref={printRef}
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: previewContent }}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>Selecione um aluno e clique em "Gerar" para visualizar</p>
                    </div>
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Fechar
            </Button>
            <Button 
              variant="outline" 
              onClick={handlePrint}
              disabled={!previewContent}
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
            <Button 
              variant="outline"
              onClick={handleDownloadPDF}
              disabled={!previewContent}
            >
              <Download className="w-4 h-4 mr-2" />
              Baixar PDF
            </Button>
            <Button 
              onClick={handleSendWhatsApp}
              disabled={!previewContent}
              className="bg-green-600 hover:bg-green-700"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Enviar WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Dialog */}
      <Dialog open={whatsappDialogOpen} onOpenChange={setWhatsappDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-600" />
              Enviar por WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Clique no botão abaixo para abrir o WhatsApp e enviar o documento para o responsável.
            </p>
            <Button 
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={() => window.open(whatsappLink, '_blank')}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Abrir WhatsApp
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* V6.10 · Modal Importar Template */}
      <ImportTemplateDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        groups={groups}
        onImported={() => { setImportDialogOpen(false); loadData(); }}
      />
    </div>
  );
}

// V6.10 · Modal de importação de template (DOCX/PDF → template editável)
function ImportTemplateDialog({ open, onClose, groups, onImported }) {
  const [step, setStep] = useState('upload'); // upload | preview
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [group, setGroup] = useState('Matrículas e Rematrículas');
  const [description, setDescription] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null); // {source_type, html, detected}

  const reset = () => { setStep('upload'); setFile(null); setName(''); setDescription(''); setAnalysis(null); };

  const doAnalyze = async () => {
    if (!file) { toast.error('Selecione um arquivo .docx ou .pdf'); return; }
    if (!name.trim()) { toast.error('Informe o nome do template'); return; }
    setAnalyzing(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/document-templates/import/analyze', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAnalysis(data);
      setStep('preview');
      toast.success(`${(data.detected?.formal || []).length} campo(s) formais + ${(data.detected?.suggestions || []).length} sugestão(ões)`);
    } catch (err) { toast.error('Falha: ' + (err?.response?.data?.detail || err.message)); }
    finally { setAnalyzing(false); }
  };

  const doSave = async () => {
    setSaving(true);
    try {
      await api.post('/document-templates/import/confirm', {
        name, group, description, content: analysis.html,
        source_type: analysis.source_type,
        original_filename: analysis.filename,
        applied_placeholders: [
          ...(analysis.detected?.formal || []),
          ...(analysis.detected?.suggestions || []).map((s) => s.placeholder),
        ],
      });
      toast.success('Template importado com sucesso.');
      reset();
      onImported && onImported();
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Importar Template</DialogTitle>
          <DialogDescription>Envie um documento .docx ou .pdf. O sistema converte para editável e detecta placeholders «CamelCase».</DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome do Template *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Contrato Educacional 2027" data-testid="import-name" />
              </div>
              <div>
                <Label>Grupo *</Label>
                <Select value={group} onValueChange={setGroup}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(groups || ['Matrículas e Rematrículas']).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Descrição (opcional)</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Breve descrição" />
            </div>
            <div className="border-2 border-dashed rounded p-6 text-center bg-purple-50/40">
              <Upload className="w-8 h-8 mx-auto text-purple-500 mb-2" />
              <input type="file" accept=".docx,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-sm" data-testid="import-file" />
              {file && <div className="text-xs mt-2 text-muted-foreground">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={doAnalyze} disabled={analyzing} className="bg-purple-600 hover:bg-purple-700" data-testid="import-analyze">
                {analyzing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Analisar documento
              </Button>
            </div>
          </div>
        )}

        {step === 'preview' && analysis && (
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 border rounded p-3 max-h-[60vh] overflow-y-auto bg-white" data-testid="import-preview">
              <div dangerouslySetInnerHTML={{ __html: analysis.html }} />
            </div>
            <div className="border rounded p-3 max-h-[60vh] overflow-y-auto text-sm space-y-3">
              <div>
                <div className="text-xs font-semibold text-muted-foreground">Campos formais detectados</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(analysis.detected?.formal || []).length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                  {(analysis.detected?.formal || []).map((p) => (
                    <Badge key={p} variant="outline" className="bg-green-50 text-green-700">«{p}»</Badge>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-muted-foreground">Sugestões (heurística)</div>
                {(analysis.detected?.suggestions || []).length === 0 && <span className="text-xs text-muted-foreground">Nenhuma</span>}
                <div className="space-y-1 mt-1">
                  {(analysis.detected?.suggestions || []).map((s, i) => (
                    <div key={i} className="text-xs border rounded p-2">
                      <div>«<b>{s.placeholder}</b>»</div>
                      <div className="text-muted-foreground">Encontrado em: <code>{s.matched_hint}</code></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t pt-2">
                <div className="text-xs text-muted-foreground">Origem: {analysis.source_type?.toUpperCase()} · {analysis.filename}</div>
              </div>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <DialogFooter>
            <Button variant="outline" onClick={() => setStep('upload')}>Voltar</Button>
            <Button onClick={doSave} disabled={saving} data-testid="import-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar Template
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
